import { useDebouncedAsyncSelect } from '@diamond/shared/context';
import type { SelectOption } from '@diamond/shared/types';
import {
  reactSelectStyles,
  SelectAutocomplete,
  Text,
} from '@diamond/shared/ui';
import { adminCaptureException } from '@diamond/shared/utils';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import axios from 'axios';
import { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { getVoucherProducts } from '../../api';
import { formatProductOption } from '../../utils/mappers';

const mergeWithSelectedOption = (
  items: SelectOption[],
  selected: SelectOption | null
) => {
  if (!selected?.value) {
    return items;
  }

  const exists = items.some((option) => option.value === selected.value);
  if (exists) {
    return items.map((option) =>
      option.value === selected.value ? selected : option
    );
  }

  return [selected, ...items];
};

export interface ProductPickerProps {
  value: SelectOption | null;
  onChange: (option: SelectOption | null) => void;
  placeholder?: string;
  debounce?: number;
  isDisabled?: boolean;
  divisions?: string[];
}

export function ProductPicker({
  value,
  onChange,
  placeholder = 'Cari nama brand, produk, kode material',
  debounce = 150, // Reduce debounce from 300ms to 150ms for faster response
  isDisabled = false,
  divisions,
}: ProductPickerProps) {
  const token = useAuthStore((s) => s.access_token);

  // Create a temporary form for SelectAutocomplete
  const methods = useForm({
    defaultValues: {
      product: value?.value || '',
    },
  });

  // Fetch function for the custom hook
  const fetchProducts = useCallback(
    async (query: string): Promise<SelectOption[]> => {
      if (!token) {
        return [];
      }

      try {
        const response = await getVoucherProducts(token, {
          page: 1,
          size: 50, // Increase from 20 to 50 for better search results
          search_query: query,
          // Pass divisions array directly (schema expects string[])
          ...(divisions && divisions.length > 0 && { divisions }),
        });

        return response.data.map(formatProductOption);
      } catch (error) {
        const respData = (
          axios.isAxiosError(error) ? error.response?.data ?? {} : {}
        ) as { statusCode?: number; message?: string };

        adminCaptureException(
          {
            message:
              respData.message ??
              (error instanceof Error ? error.message : 'Unknown error'),
            statusCode:
              respData.statusCode ??
              (axios.isAxiosError(error) ? error.response?.status ?? 0 : 0),
          },
          (scope) => {
            scope.setTags({
              feature: 'voucher',
              action: 'productPickerLoad',
              status_code: respData.statusCode,
            });
            scope.setContext('error', { error: respData });
            scope.setLevel('error');
          }
        );
        throw error;
      }
    },
    [token, divisions]
  );

  // Use custom debounced async select hook
  // Set minQueryLength to 2 to avoid unnecessary API calls on empty/short queries
  const { options, setOptions, loadOptions } = useDebouncedAsyncSelect({
    fetchFn: fetchProducts,
    getEmptyResult: useCallback(() => [], []),
    debounceMs: debounce,
    minQueryLength: 2,
  });

  // Sync options with selected value
  useEffect(() => {
    setOptions((currentOptions) =>
      mergeWithSelectedOption(currentOptions, value)
    );
  }, [value, setOptions]);

  // Keep form control in sync with external value
  useEffect(() => {
    methods.setValue('product', value?.value || '');
  }, [value, methods]);

  const handleChange = useCallback(
    (value: string | null, option?: SelectOption) => {
      onChange(option || null);
    },
    [onChange]
  );

  return (
    <FormProvider {...methods}>
      <SelectAutocomplete
        name="product"
        placeholder={placeholder}
        emptyMessage={<Text>Produk tidak ditemukan</Text>}
        debounce={debounce}
        chakraStyles={reactSelectStyles}
        onChange={handleChange}
        loadOptions={loadOptions}
        options={options}
        isDisabled={isDisabled}
      />
    </FormProvider>
  );
}
